<template>
  <div class="home-stage" :class="rootClasses" :style="rootStyle">
    <div class="home-stage__container">
      <h1 class="home-stage__headline">
        {{ stageHeadline }}
      </h1>
      <p class="home-stage__lead-copy" v-if="stageCopy">
        {{ stageCopy }}
      </p>
      <search-bar
        class="home-stage__search-bar"
        bar-size="medium"
        item-position="home-stage" />
      <p v-if="sliderHeadline">
        {{ sliderHeadline }}
      </p>
      <div class="home-stage__slider">
        <slot />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useImage } from "#image/composables";
import { computed, ref, onMounted } from "#imports";

const props = defineProps<{
  stageHeadline: string,
  sliderHeadline: string,
  stageCopy: string,
  imageUrl: string,
  blackColor?: boolean,
}>();

const img = useImage();

const showPlaceholder = ref(false);
const imageLoaded = ref(false);

const rootClasses = computed(() => {
  return {
    'home-stage--with-bg': !!props.imageUrl,
    'home-stage--color-black': props.blackColor,
  };
});

const rootStyle = computed(() => {
  if (imageSource.value) {
    return {
      '--bg-img-url': `url(${imageSource.value})`,
    };
  }

  return null;
});

const optimizedSource = computed<string>(() => {
  return img(props.imageUrl, {
    format: 'avif',
    height: 1080,
    width: 1920,
  });
});

const imageSource = computed(() => {
  let url = null;

  if (imageLoaded.value) {
    url = optimizedSource.value
  }

  if (showPlaceholder.value) {
    url = props.imageUrl;
  }

  return url;
});

// Load image in js first because it's harder to detect if image can be loaded if it's displayed in css only
async function loadImage (): void {
  const image = new Image();
  image.onload = () => {
    imageLoaded.value = true;
  }
  image.onerror = () => {
    showPlaceholder.value = true;
  }
  image.src = optimizedSource.value;
}

onMounted (() => {
  if (props.imageUrl) {
    loadImage();
  }
})
</script>

<style lang="scss">
@use "~/common/scss/variables";
@use "~/common/scss/mq";
@use "~/common/scss/typography";
@use "~/common/scss/helpers";

.home-stage {
  color: #FFF;
  background-color: variables.$colorContrast;
  text-wrap: balance;
  padding: 6rem 0 1rem;

  @include mq.mobileTablet() {
    padding: 2rem 0 0.1rem;
  }

  &--with-bg {
    background-image: var(--bg-img-url);
    background-position: 50% 50%;
    background-size: cover;
  }

  &--color-black {
    color: #000;
  }

  &__container {
    margin: 0 auto;
    width: 95%;

    @include mq.aboveTablet() {
      width: 86.5%;
    }
  }

  &__headline {
    color: currentColor;
    @include mq.mobileOnly() {
      font-size: 2.0rem;
    }
  }

  &__lead-copy {
    @include typography.leadBody();

    min-height: helpers.px-to-rem(70);
    margin-bottom: helpers.px-to-rem(8);

    @include mq.mobileOnly() {
      font-size: 1.5rem;
    }
  }

  &__search-bar {
    margin-bottom: helpers.px-to-rem(70);

    .typeahead-results {
      left: 0;
      right: unset;

      @include mq.aboveTablet() {
        max-height: max(calc(100vh - #{helpers.px-to-rem(500)}), #{helpers.px-to-rem(350)});
      }
    }
  }

  .item-slider__navigation-icon,
  .item-slider__see-all,
  .item-slider__carousel-title {
    color: variables.$colorWhite;
  }
}
</style>
